import * as Yup from 'yup'
import REGEX from '../constants/regex.constants'
import * as CPFService from '../services/cpf.service'
import moment from 'moment'
import { dateHelper } from './date.helper'

/**
 * @param {Yup.ref} ref
 * Yup ref to the field that must be matched
 *
 * @param {string} msg
 * Error massage case the field doesnt match
 */
const equalTo = (ref, msg) => {
	return Yup.mixed().test({
		name: 'equalTo',
		exclusive: false,
		message: msg || '${path} deve ser igual à ${reference}',
		params: {
			reference: ref.path,
		},
		test: function (value) {
			return !value || value === this.resolve(ref)
		},
	})
}

Yup.addMethod(Yup.string, 'equalTo', equalTo)

/**
 * @param {Yup.ref} ref
 * Yup ref to the field that must be matched
 *
 * @param {string} msg
 * Error massage case the field doesnt match
 */
const equalToRequired = (ref, msg) => {
	return Yup.mixed().test({
		name: 'equalTo',
		exclusive: false,
		message: msg || '${path} deve ser igual à ${reference}',
		params: {
			reference: ref.path,
		},
		test: function (value) {
			return value === this.resolve(ref)
		},
	})
}

Yup.addMethod(Yup.string, 'equalToRequired', equalToRequired)

/**
 * Name validations
 * 	Should be non numeric
 * 	Should be full name
 */
const name = () =>
	Yup.string()
		.trim()
		.matches(REGEX.NAME_NON_NUMERIC, 'Nome inválido')
		.matches(REGEX.NAME_FULL, 'Informe o nome completo')

Yup.addMethod(Yup.string, 'name', name)

/**
 * CPF validations
 */
const cpf = () =>
	Yup.string().test(
		'len',
		'CPF inválido',
		(val) => !val || CPFService.validate(val)
	)

Yup.addMethod(Yup.string, 'cpf', cpf)

/**
 * RG validations
 */
const rg = () =>
	Yup.string()
		.matches(REGEX.RG, 'RG inválido')
		.test(
			'len',
			'RG inválido',
			(val) => !val || (val.length >= 6 && val.length <= 14)
		)

Yup.addMethod(Yup.string, 'rg', rg)

/**
 * Birthday validations
 */
const birthday = () =>
	Yup.string().test(
		'len',
		'Data de nascimento inválida',
		(val) => {
			if (!val || val.length < 10) return true
			let date = moment(val, 'YYYY-MM-DD', true)

			if (!date.isValid()) {
				date = moment(val, 'DD/MM/YYYY', true)
			}

			return date.isValid() && !date.isAfter(moment().startOf('day'))
		}
	)

Yup.addMethod(Yup.string, 'birthday', birthday)

/**
 * Future date validations (DD/MM/YYYY)
 * Must be a valid date and not in the past
 */
const futureDate = () =>
	Yup.string().test(
		'futureDate',
		'Data inválida ou anterior à data atual',
		(val) => dateHelper.isValidFutureDate(val)
	)

Yup.addMethod(Yup.string, 'futureDate', futureDate)

/**
 * Phone validations
 * Works to Landline and Telphone
 */
const phone = () => Yup.string().matches(REGEX.PHONE, 'Telefone inválido')

Yup.addMethod(Yup.string, 'phone', phone)

/**
 * Phone validations
 * Works to Landline and Cellphone
 */
const cellphone = () =>
	Yup.string()
		.test('len', 'Favor digitar ao menos 11 dígitos incluindo DDD', (val) => {
			if (!val) return true
			const digits = val.replace(/\D/g, '')
			return digits.length >= 11
		})
		.test('format', 'Celular inválido', (val) => {
			if (!val) return true
			const digits = val.replace(/\D/g, '')
			if (digits.length === 11) return true
			return REGEX.CELLPHONE.test(val)
		})

Yup.addMethod(Yup.string, 'cellphone', cellphone)

const password = () =>
	Yup.string()
		.matches(REGEX.PASSWORD_MIN_LENGTH, 'Tamanho mínimo de 12 caracteres')
		.matches(
			REGEX.PASSWORD_UPPERCASE,
			'Deve conter pelo menos uma letra maiúscula'
		)
		.matches(
			REGEX.PASSWORD_LOWERCASE,
			'Deve conter pelo menos uma letra minúscula'
		)
		.matches(
			REGEX.PASSWORD_NUMERIC,
			'Deve conter pelo menos um caractere numérico'
		)
		.matches(
			REGEX.PASSWORD_SPECIAL_CHAR,
			'Deve conter pelo menos um caractere especial'
		)
		.required('A senha é obrigatória')

Yup.addMethod(Yup.string, 'password', password)

/**
 * Password Card validations
 */
const passwordCard = () =>
	Yup.string()
		.min(4, 'Tamanho mínimo de 4 caracteres')
		.matches(
			REGEX.PASSWORD_NUMERIC,
			'Deve conter apenas caracteres numéricos'
		)

Yup.addMethod(Yup.string, 'passwordCard', passwordCard)

/**
 * PostalCode validations
 */
const postalCode = () =>
	Yup.string().test(
		'len',
		'CEP inválido',
		(val) => !val || val.replace(/\-/g, '').length === 8
	)

Yup.addMethod(Yup.string, 'postalCode', postalCode)

/**
 * Number higher than zero validations
 */
const residenceNumber = () =>
	Yup.string().test(
		'len',
		'Número inválido',
		(val) =>
			!val ||
			(val.replace(/[^0-9]/g, '') > 0 &&
				val.replace(/[^0-9]/g, '') < 1000000000)
	)

Yup.addMethod(Yup.string, 'residenceNumber', residenceNumber)

const shouldBeTrue = (msg) =>
	Yup.boolean().test('shouldBeTrue', msg, (val) => val)

Yup.addMethod(Yup.boolean, 'shouldBeTrue', shouldBeTrue)

/**
 * SecurityKey validations
 */
const securityKey = () =>
	Yup.string().matches(REGEX.SECURITY_KEY, 'Chave de segurança inválida')

Yup.addMethod(Yup.string, 'securityKey', securityKey)

/**
 * Email Moveflex validations
 */
const emailMoveflex = () =>
	Yup.string().matches(
		REGEX.EMAIL_MOVEFLEX,
		'O e-mail deve pertencer a um dos domínios Muviflex'
	)

Yup.addMethod(Yup.string, 'emailMoveflex', emailMoveflex)

export default Yup
